"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportClientError, reportClientWarning, type ClientReportTags } from "@/lib/clientReporting";
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: (retry: () => void) => ReactNode;
  tags: ClientReportTags;
  reportLevel?: "error" | "warning";
  onError?: (error: Error) => void;
}
interface ErrorBoundaryState {
  hasError: boolean;
}
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false
  };
  static getDerivedStateFromError(): ErrorBoundaryState {
    return {
      hasError: true
    };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const report = this.props.reportLevel === "warning" ? reportClientWarning : reportClientError;
    report({
      message: error.message || `${this.props.tags.surface} boundary caught an error`,
      cause: error,
      data: {
        componentStack: errorInfo.componentStack
      },
      tags: this.props.tags
    });
    this.props.onError?.(error);
  }
  handleRetry = (): void => {
    this.setState({
      hasError: false
    });
  };
  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback(this.handleRetry);
    }
    return this.props.children;
  }
}